import React, { useState, useEffect } from 'react';
import { Building2, Users, Calendar, CreditCard, CheckCircle, XCircle, ArrowRight, Power, PowerOff, Trash2, Plus, Activity, Edit2, UserPlus, ExternalLink } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import CreateOrganizationModal from './CreateOrganizationModal';
import EditOrganizationModal from './EditOrganizationModal';
import OrganizationHealthModal from './OrganizationHealthModal';
import BulkActionsModal from './BulkActionsModal';
import TransferOwnershipModal from './TransferOwnershipModal';


export default function AllOrganizationsTable({ onSwitchOrganization }) {
  const { profile } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);

  useEffect(() => {
    loadOrganizations();
    
    // Real-time subscription for organization changes
    const channel = supabase?.channel('organizations-changes')?.on('postgres_changes', 
        { event: '*', schema: 'public', table: 'tenants' },
        () => {
          loadOrganizations();
        }
      )?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'user_organization_memberships' },
        () => {
          loadOrganizations();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all organizations with member counts and subscription info
      const { data: orgsData, error: orgsError } = await supabase?.from('tenants')?.select(`
          id,
          name,
          subdomain,
          logo_url,
          is_active,
          created_at,
          industry:company_industries(name),
          subscription:tenant_subscriptions(
            status,
            tier:subscription_tiers(name)
          )
        `)?.order('created_at', { ascending: false });

      if (orgsError) throw orgsError;

      // Get member counts for each organization
      const orgsWithCounts = await Promise.all(
        orgsData?.map(async (org) => {
          const { count, error: countError } = await supabase?.from('user_organization_memberships')?.select('*', { count: 'exact', head: true })?.eq('tenant_id', org?.id);

          return {
            id: org?.id,
            name: org?.name,
            subdomain: org?.subdomain,
            logoUrl: org?.logo_url,
            isActive: org?.is_active,
            createdAt: org?.created_at,
            industryName: org?.industry?.name || 'Not specified',
            memberCount: count || 0,
            subscriptionStatus: org?.subscription?.[0]?.status || 'inactive',
            subscriptionTier: org?.subscription?.[0]?.tier?.name || 'Free'
          };
        }) || []
      );

      setOrganizations(orgsWithCounts);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError(err?.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchOrganization = async (tenantId) => {
    try {
      setSwitching(tenantId);
      await onSwitchOrganization?.(tenantId);
    } catch (err) {
      console.error('Error switching organization:', err);
      setError(err?.message || 'Failed to switch organization');
    } finally {
      setSwitching(null);
    }
  };

  const handleCreateOrganization = async (data) => {
    try {
      const { error } = await supabase?.from('tenants')?.insert(data);
      if (error) throw error;
      
      loadOrganizations();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err?.message || 'Failed to create organization');
    }
  };

  const handleEditOrganization = async (data) => {
    try {
      const { error } = await supabase?.from('tenants')?.update(data)?.eq('id', selectedOrg?.id);
      if (error) throw error;
      
      loadOrganizations();
      setShowEditModal(false);
      setSelectedOrg(null);
    } catch (err) {
      console.error('Error editing organization:', err);
      setError(err?.message || 'Failed to edit organization');
    }
  };

  const handleActivateOrganization = async (org) => {
    try {
      const { error } = await supabase?.from('tenants')?.update({ is_active: true })?.eq('id', org?.id);
      if (error) throw error;
      
      loadOrganizations();
    } catch (err) {
      console.error('Error activating organization:', err);
      setError(err?.message || 'Failed to activate organization');
    }
  };

  const handleSuspendOrganization = async (org) => {
    try {
      const { error } = await supabase?.from('tenants')?.update({ is_active: false })?.eq('id', org?.id);
      if (error) throw error;
      
      loadOrganizations();
    } catch (err) {
      console.error('Error suspending organization:', err);
      setError(err?.message || 'Failed to suspend organization');
    }
  };

  const handleDeleteOrganization = async (org) => {
    try {
      const { error } = await supabase?.from('tenants')?.delete()?.eq('id', org?.id);
      if (error) throw error;
      
      loadOrganizations();
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err?.message || 'Failed to delete organization');
    }
  };

  const handleTransferOwnership = async (data) => {
    try {
      const { error } = await supabase?.from('user_organization_memberships')?.update(data);
      if (error) throw error;
      
      loadOrganizations();
      setShowTransferModal(false);
      setSelectedOrg(null);
    } catch (err) {
      console.error('Error transferring ownership:', err);
      setError(err?.message || 'Failed to transfer ownership');
    }
  };

  const handleBulkAction = async () => {
    try {
      if (bulkAction === 'activate') {
        await Promise.all(selectedOrgs?.map(async (org) => {
          const { error } = await supabase?.from('tenants')?.update({ is_active: true })?.eq('id', org?.id);
          if (error) throw error;
        }));
      } else if (bulkAction === 'suspend') {
        await Promise.all(selectedOrgs?.map(async (org) => {
          const { error } = await supabase?.from('tenants')?.update({ is_active: false })?.eq('id', org?.id);
          if (error) throw error;
        }));
      } else if (bulkAction === 'delete') {
        await Promise.all(selectedOrgs?.map(async (org) => {
          const { error } = await supabase?.from('tenants')?.delete()?.eq('id', org?.id);
          if (error) throw error;
        }));
      }
      
      loadOrganizations();
      setShowBulkModal(false);
      setBulkAction('');
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError(err?.message || 'Failed to perform bulk action');
    }
  };

  const toggleSelectAll = () => {
    if (selectedOrgs?.length === organizations?.length && organizations?.length > 0) {
      setSelectedOrgs([]);
    } else {
      setSelectedOrgs(organizations?.map(org => org));
    }
  };

  const toggleSelectOrg = (org) => {
    const isSelected = selectedOrgs?.some(o => o?.id === org?.id);
    if (isSelected) {
      setSelectedOrgs(selectedOrgs?.filter(o => o?.id !== org?.id));
    } else {
      setSelectedOrgs([...selectedOrgs, org]);
    }
  };

  const openBulkModal = (action) => {
    setBulkAction(action);
    setShowBulkModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      trial: { color: 'bg-blue-100 text-blue-800', icon: Calendar, label: 'Trial' },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Suspended' },
      cancelled: { color: 'bg-orange-100 text-orange-800', icon: XCircle, label: 'Cancelled' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    const Icon = config?.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon className="w-3 h-3" />
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleOpenTenantUrl = (subdomain) => {
    // Get the base domain from current hostname
    const hostname = window?.location?.hostname;
    let baseDomain = 'localprocrm.com';
    
    // If on Rocket development, use the production domain
    if (!hostname?.includes('.builtwithrocket.new') && hostname !== 'localhost' && hostname !== '127.0.0.1') {
      // Extract base domain from current hostname (remove subdomain if present)
      const parts = hostname?.split('.');
      if (parts?.length > 2) {
        baseDomain = parts?.slice(-2)?.join('.');
      } else {
        baseDomain = hostname;
      }
    }
    
    // Construct tenant URL
    const tenantUrl = `https://${subdomain}.${baseDomain}`;
    
    // Open in new tab
    window.open(tenantUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 font-medium">Error loading organizations</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrganizations}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">All Organizations</h2>
              <p className="text-sm text-gray-600 mt-1">
                Total: {organizations?.length} organization{organizations?.length !== 1 ? 's' : ''}
                {selectedOrgs?.length > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    ({selectedOrgs?.length} selected)
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedOrgs?.length > 0 && (
                <>
                  <button
                    onClick={() => openBulkModal('activate')}
                    className="px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors flex items-center gap-2"
                  >
                    <Power className="w-4 h-4" />
                    Activate
                  </button>
                  <button
                    onClick={() => openBulkModal('suspend')}
                    className="px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors flex items-center gap-2"
                  >
                    <PowerOff className="w-4 h-4" />
                    Suspend
                  </button>
                  <button
                    onClick={() => openBulkModal('delete')}
                    className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </>
              )}
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Organization
              </button>
              <button
                onClick={loadOrganizations}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedOrgs?.length === organizations?.length && organizations?.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Industry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {organizations?.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">No organizations found</p>
                    <p className="text-gray-500 text-sm mt-1">Organizations will appear here once created</p>
                  </td>
                </tr>
              ) : (
                organizations?.map((org) => (
                  <tr key={org?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOrgs?.some(o => o?.id === org?.id)}
                        onChange={() => toggleSelectOrg(org)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {org?.logoUrl ? (
                          <img
                            src={org?.logoUrl}
                            alt={`${org?.name} logo`}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{org?.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{org?.subdomain}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{org?.industryName}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{org?.memberCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{org?.subscriptionTier}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(org?.subscriptionStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{formatDate(org?.createdAt)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenTenantUrl(org?.subdomain)}
                          className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                          title="Open Tenant URL"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrg(org);
                            setShowHealthModal(true);
                          }}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="View Health"
                        >
                          <Activity className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrg(org);
                            setShowEditModal(true);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {org?.isActive ? (
                          <button
                            onClick={() => handleSuspendOrganization(org)}
                            className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                            title="Suspend"
                          >
                            <PowerOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateOrganization(org)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Activate"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedOrg(org);
                            setShowTransferModal(true);
                          }}
                          className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="Transfer Ownership"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOrganization(org)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSwitchOrganization(org?.id)}
                          disabled={switching === org?.id}
                          className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-50"
                          title="Switch to Organization"
                        >
                          {switching === org?.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                          ) : (
                            <ArrowRight className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateOrganization}
      />

      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedOrg(null);
        }}
        onSave={handleEditOrganization}
        organization={selectedOrg}
      />

      <OrganizationHealthModal
        isOpen={showHealthModal}
        onClose={() => {
          setShowHealthModal(false);
          setSelectedOrg(null);
        }}
        organization={selectedOrg}
      />

      <BulkActionsModal
        isOpen={showBulkModal}
        onClose={() => {
          setShowBulkModal(false);
          setBulkAction('');
        }}
        selectedOrgs={selectedOrgs}
        action={bulkAction}
        onConfirm={handleBulkAction}
      />

      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedOrg(null);
        }}
        onSave={handleTransferOwnership}
        organization={selectedOrg}
      />
    </>
  );
}